/**
 * Shared document export used everywhere the user can author a document
 * (Design Docs, Triage reports, …). Markdown is the source of truth; we derive
 * plain text and a Word-openable .doc from it. Fully client-side, no deps.
 */

export type ExportFormat = "md" | "txt" | "doc";

export const EXPORT_FORMATS: { id: ExportFormat; label: string }[] = [
  { id: "md", label: "Markdown (.md)" },
  { id: "doc", label: "Word (.doc)" },
  { id: "txt", label: "Plain text (.txt)" },
];

function slug(s: string): string {
  return s.trim().replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "document";
}

function download(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/** Strip Markdown formatting to readable plain text. */
export function mdToPlainText(md: string): string {
  return md
    .replace(/```([\s\S]*?)```/g, (_m, c) => c)
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^\s*[-*]\s+/gm, "• ")
    .replace(/^\s*\|.*\|\s*$/gm, (row) => row.replace(/\|/g, "  ").trim())
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Minimal, safe Markdown → HTML for Word export (headings, lists, emphasis, links, tables). */
export function mdToHtml(md: string): string {
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s: string) =>
    esc(s)
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/\*([^*]+)\*/g, "<em>$1</em>")
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  let list: null | "ul" | "ol" = null;
  let table: string[][] | null = null;

  const closeList = () => {
    if (list) {
      out.push(`</${list}>`);
      list = null;
    }
  };
  const flushTable = () => {
    if (!table) return;
    const [head, , ...rows] = table; // second row is the |---| separator
    out.push("<table border='1' cellspacing='0' cellpadding='4'>");
    if (head) out.push("<tr>" + head.map((c) => `<th>${inline(c)}</th>`).join("") + "</tr>");
    for (const r of rows) out.push("<tr>" + r.map((c) => `<td>${inline(c)}</td>`).join("") + "</tr>");
    out.push("</table>");
    table = null;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const isTableRow = /^\s*\|.*\|\s*$/.test(line);
    if (isTableRow) {
      closeList();
      const cells = line.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      (table ??= []).push(cells);
      continue;
    }
    flushTable();

    const h = line.match(/^(#{1,6})\s+(.*)$/);
    const ul = line.match(/^\s*[-*]\s+(.*)$/);
    const ol = line.match(/^\s*\d+\.\s+(.*)$/);
    if (h) {
      closeList();
      out.push(`<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`);
    } else if (ul) {
      if (list !== "ul") { closeList(); out.push("<ul>"); list = "ul"; }
      out.push(`<li>${inline(ul[1])}</li>`);
    } else if (ol) {
      if (list !== "ol") { closeList(); out.push("<ol>"); list = "ol"; }
      out.push(`<li>${inline(ol[1])}</li>`);
    } else if (!line.trim()) {
      closeList();
    } else {
      closeList();
      out.push(`<p>${inline(line)}</p>`);
    }
  }
  closeList();
  flushTable();
  return out.join("\n");
}

/** Export `markdown` as the chosen format, downloading `<baseName>.<ext>`. */
export function exportDocument(baseName: string, markdown: string, format: ExportFormat): void {
  const name = slug(baseName);
  if (format === "md") {
    download(`${name}.md`, new Blob([markdown], { type: "text/markdown" }));
  } else if (format === "txt") {
    download(`${name}.txt`, new Blob([mdToPlainText(markdown)], { type: "text/plain" }));
  } else {
    const html =
      `<!DOCTYPE html><html xmlns:o="urn:schemas-microsoft-com:office:office" ` +
      `xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">` +
      `<head><meta charset="utf-8"><title>${slug(baseName)}</title></head><body>${mdToHtml(markdown)}</body></html>`;
    // BOM helps Word detect UTF-8; .doc opens in Word and imports into Google Docs.
    download(`${name}.doc`, new Blob(["﻿", html], { type: "application/msword" }));
  }
}
